import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import classNames from 'classnames';
import { useState } from 'react';
import EditPositionModal from 'components/IncidentConfig/EditPositionModal';
import Tree from 'components/DesignSystems/OrganizationChart/Tree';
import TreeNode, {
  TreeNodeVariant,
  TreeNodeVariantStrings,
} from 'components/DesignSystems/OrganizationChart/TreeNode';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';

type Section = {
  name: string;
  borderColor: string;
  sectionSeatName: string;
};

const Sections = [
  {
    name: 'Operations Section',
    borderColor: '#ea0e0e',
    sectionSeatName: 'Operations Section Chief',
  },
  {
    name: 'Planning Section',
    borderColor: '#4273d5',
    sectionSeatName: 'Planning Section Chief',
  },
  {
    name: 'Logistics Section',
    borderColor: '#fb9905',
    sectionSeatName: 'Logistics Section Chief',
  },
  {
    name: 'Finance/Admin Section',
    borderColor: '#38ca22',
    sectionSeatName: 'Finance Section Chief',
  },
  {
    name: 'Intel/Investigations Section',
    borderColor: '#d6802a63',
    sectionSeatName: 'Intel/Investigations Section Chief',
  },
  {
    name: 'Command Staff',
    borderColor: 'none',
    sectionSeatName: 'Incident Commander',
  },
];

type Position = {
  positionId: string;
  incidentSeat: string;
  parentSeat: string | null;
  isWorkgroup: boolean;
};

type SeatBoxProps = {
  positionId: string;
  members: RosterEntry[];
  setShowEditPositionModal: (positionId: string) => void;
  seatName?: string;
  currentSection?: Section | null;
};

const WorkTeamSeatBox = ({
  positionId,
  members,
  seatName,
  currentSection,
  setShowEditPositionModal,
}: SeatBoxProps) => {
  const leaders = members.filter(
    (member) =>
      member.incidentPositions.find((pos) => pos.positionId === positionId)
        ?.isLeader
  );
  const nonLeaders = members.filter(
    (member) =>
      !member.incidentPositions.find((pos) => pos.positionId === positionId)
        ?.isLeader
  );
  return (
    <div
      className={classNames('chartBox')}
      title={'Workgroup'}
      role="button"
      onClick={(e) => {
        e.stopPropagation();
        setShowEditPositionModal(positionId);
      }}
      style={{
        borderColor: currentSection?.borderColor || '#000',
        borderWidth: '3px',
      }}
    >
      {seatName && <div className="chartBox__role">{seatName}</div>}
      {leaders.length > 0 && nonLeaders.length > 0 && <div>Leader:</div>}
      {leaders.length === 1 ? (
        <div
          key={leaders[0].userId || leaders[0].incidentMemberId || `leader-0`}
          className="chartBox__person chartBox__person--leader"
        >
          {leaders[0].active === 1 && leaders[0].activeTime && <span>*</span>}
          {leaders[0].userName || leaders[0].emailAddress}
        </div>
      ) : (
        leaders.length > 0 && (
          <div
            key={`leaders`}
            className="chartBox__person chartBox__person--leader"
          >
            {leaders.length} LEADERS
          </div>
        )
      )}
      {leaders.length > 0 && nonLeaders.length > 0 && (
        <div className="chartBox__separator" />
      )}

      {nonLeaders.length === 1 ? (
        <div
          key={
            nonLeaders[0].userId || nonLeaders[0].incidentMemberId || `member-0`
          }
          className="chartBox__person"
        >
          {nonLeaders[0].active === 1 && nonLeaders[0].activeTime && (
            <span>*</span>
          )}
          {nonLeaders[0].userName || nonLeaders[0].emailAddress}
        </div>
      ) : (
        <div
          key={`nonLeaders-${nonLeaders.length}`}
          className="chartBox__person"
        >
          {nonLeaders.length} ASSIGNED
        </div>
      )}
    </div>
  );
};

const SeatBox = ({
  positionId,
  members,
  seatName,
  currentSection,
  setShowEditPositionModal,
}: SeatBoxProps) => {
  let borderColor = '#000';

  if (currentSection) {
    borderColor = currentSection.borderColor;
  }

  if (seatName?.includes('Technical Specialist')) {
    borderColor = '#FFFF00';
  }

  return (
    <div
      className={classNames('chartBox')}
      role="button"
      onClick={(e) => {
        e.stopPropagation();
        setShowEditPositionModal(positionId);
      }}
      style={{
        borderColor,
        borderWidth: '3px',
      }}
    >
      {seatName && <div className="chartBox__role">{seatName}</div>}
      {members.length === 1 ? (
        <div
          key={members[0].userId || members[0].incidentMemberId || `member-0`}
          className="chartBox__person chartBox__person--leader"
        >
          {members[0].active === 1 && members[0].activeTime && <span>*</span>}
          {members[0].userName || members[0].emailAddress}
        </div>
      ) : (
        <div
          key={`members-${members.length}`}
          className="chartBox__person chartBox__person--leader"
        >
          {members.length} ASSIGNED
        </div>
      )}
    </div>
  );
};

const buildSeatBox = (props: SeatBoxProps & { isWorkteam: boolean }) => {
  if (props.isWorkteam) {
    return <WorkTeamSeatBox {...props} />;
  } else {
    return <SeatBox {...props} />;
  }
};

const checkIfVertical = (seatName: string) => {
  if (seatName.includes('Technical Specialist')) {
    return true;
  }

  switch (seatName) {
    case 'Compensation Unit Leader':
    case 'Cost Unit Leader':
    case 'Procurement Unit Leader':
    case 'Time Unit Leader':
    case 'Communications Unit Leader':
    case 'Food Unit Leader':
    case 'Medical Unit Leader':
    case 'Facilities Unit Leader':
    case 'Ground Support Unit Leader':
    case 'Supply Unit Leader':
    case 'Demobilization Unit Leader':
    case 'Documentation Unit Leader':
    case 'Resource Unit Leader':
    case 'Situation Unit Leader':
    case 'Investigative Operations Group Supervisor':
    case 'Intelligence Group Supervisor':
    case 'Technical Specialist':
      return true;
    default:
      return false;
  }
};

const checkIfAssistant = (seatName: string) => {
  switch (seatName) {
    case 'Legal Officer':
    case 'Liaison Officer':
    case 'Safety Officer':
    case 'Public Information Officer':
    case 'Staging Area Manager':
      return true;
    default:
      return false;
  }
};

const planningSectionOrder = [
  'Resource Unit Leader',
  'Situation Unit Leader',
  'Documentation Unit Leader',
  'Demobilization Unit Leader',
  'Environmental Unit Leader',
  'Technical Specialist',
];

const commandStaffOrder = [
  'Public Information Officer',
  'Liaison Officer',
  'Safety Officer',
  'Legal Officer',
];

const buildTree = (
  parentSeat: string | null,
  incidentMembers: RosterEntry[],
  positions: Position[],
  showAssignedPositions: boolean,
  setShowEditPositionModal: (positionId: string) => void,
  currentSection: Section | null = null,
  depth: number = 0
) => {
  const children = positions.filter((pos) => pos.parentSeat === parentSeat);

  // If any children are "section roots", sort them by the order defined in `Sections`
  const sectionOrder = new Map([
    ...Sections.map((section, idx) => [section.sectionSeatName, idx] as const),
    ...commandStaffOrder.map(
      (seatName, idx) => [seatName, Sections.length + idx] as const
    ),
  ]);

  const hasSectionChildren = children.some((c) =>
    sectionOrder.has(c.incidentSeat)
  );

  const isPlanningSection = currentSection?.name === 'Planning Section';

  if (hasSectionChildren) {
    children.sort((a, b) => {
      const aIdx = sectionOrder.get(a.incidentSeat) ?? Number.POSITIVE_INFINITY;
      const bIdx = sectionOrder.get(b.incidentSeat) ?? Number.POSITIVE_INFINITY;

      if (aIdx !== bIdx) return aIdx - bIdx;

      // Non-section (or ties) fall back to alphabetical
      return a.incidentSeat.localeCompare(b.incidentSeat);
    });
  } else if (isPlanningSection) {
    children.sort((a, b) => {
      const aIdx =
        planningSectionOrder.findIndex((name) => name === a.incidentSeat) ??
        Number.POSITIVE_INFINITY;
      const bIdx =
        planningSectionOrder.findIndex((name) => name === b.incidentSeat) ??
        Number.POSITIVE_INFINITY;

      if (aIdx !== bIdx) return aIdx - bIdx;

      // Non-section (or ties) fall back to alphabetical
      return a.incidentSeat.localeCompare(b.incidentSeat);
    });
  } else {
    children.sort((a, b) => a.incidentSeat.localeCompare(b.incidentSeat));
  }

  return children.map((child, index) => {
    const membersForSeat = incidentMembers.filter((member) =>
      member.incidentPositions?.some(
        (pos) => pos.positionId === child.positionId
      )
    );

    if (membersForSeat.length === 0) {
      // Check if there are any children with members

      const hasChildWithMembers = checkChildrenForMembers({
        parentSeat: child.incidentSeat,
        incidentMembers,
        positions,
      });

      // Note: if we comment-out this early return, then we will show all the positions, regardless of assigned members.
      if (showAssignedPositions && !hasChildWithMembers) {
        return null; // Skip this node entirely
      }
    }

    let sectionForChild: Section | undefined = Sections.find(
      (section) => section.sectionSeatName === child.incidentSeat
    );

    let variant: TreeNodeVariant = TreeNodeVariantStrings.horizontal;

    if (checkIfVertical(child.incidentSeat)) {
      variant = TreeNodeVariantStrings.vertical;
    }

    if (checkIfAssistant(child.incidentSeat)) {
      variant = TreeNodeVariantStrings.assistant;
    }

    if (child.incidentSeat) {
      return (
        <TreeNode
          key={child.incidentSeat}
          label={buildSeatBox({
            positionId: child.positionId,
            seatName: child.incidentSeat,
            members: membersForSeat,
            isWorkteam: child.isWorkgroup,
            setShowEditPositionModal,
            currentSection: sectionForChild || currentSection,
          })}
          variant={variant}
          // vertical={checkIfVertical(child.incidentSeat)}
          // assistant={checkIfAssistant(child.incidentSeat)}
          isLast={index === children.length - 1}
        >
          {buildTree(
            child.incidentSeat,
            incidentMembers,
            positions,
            showAssignedPositions,
            setShowEditPositionModal,
            sectionForChild || currentSection,
            depth + 1
          )}
        </TreeNode>
      );
    }
  });
};

const checkChildrenForMembers = ({
  parentSeat,
  incidentMembers,
  positions,
}: {
  parentSeat: string | null;
  incidentMembers: RosterEntry[];
  positions: Position[];
}): boolean => {
  const children = positions.filter((pos) => pos.parentSeat === parentSeat);

  for (const child of children) {
    const membersForSeat = incidentMembers.filter((member) =>
      member.incidentPositions?.some(
        (pos) => pos.positionId === child.positionId
      )
    );

    if (membersForSeat.length > 0) {
      return true;
    }

    if (
      checkChildrenForMembers({
        parentSeat: child.incidentSeat,
        incidentMembers,
        positions,
      })
    ) {
      return true;
    }
  }

  return false;
};

const IncidentRosterBlockChart: React.FC<{
  incidentMembers: RosterEntry[];
}> = ({ incidentMembers }) => {
  const incidentPositionsQuery = useGetPositionsForIncident();
  const incidentPositions = incidentPositionsQuery.data || [];

  const positions: Position[] = incidentPositions
    .filter((pos) => !(pos.is_template && pos.name === 'Qualified Individual'))
    .map((pos) => ({
      positionId: pos.id,
      incidentSeat: pos.name,
      parentSeat: pos.parent_name,
      isWorkgroup: pos.is_workgroup,
    }));

  const [showAssignedPositions, setShowAssignedPositions] = useState(false);
  const [showEditPositionModalForPosition, setShowEditPositionModal] =
    useState('');

  return (
    <div
      className="form-block chart"
      style={{ overflowX: 'auto', width: '100%' }}
    >
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="checkbox"
          onChange={(e) => setShowAssignedPositions(e.target.checked)}
          checked={showAssignedPositions}
        />
        Only show positions with assigned members
      </label>
      {positions
        .filter((pos) => pos.parentSeat === null)
        .sort((a, b) => a.incidentSeat.localeCompare(b.incidentSeat))
        .map((rootPosition) => {
          const membersForSeat = incidentMembers.filter((member) =>
            member.incidentPositions?.some(
              (pos) => pos.positionId === rootPosition.positionId
            )
          );

          if (membersForSeat.length === 0) {
            const hasChildWithMembers = checkChildrenForMembers({
              parentSeat: rootPosition.incidentSeat,
              incidentMembers,
              positions,
            });

            if (showAssignedPositions && !hasChildWithMembers) {
              return null; // Skip if no members for this root position and its children
            }
          }

          const currentSection =
            Sections.find(
              (section) => section.sectionSeatName === rootPosition.incidentSeat
            ) || null;

          return (
            <>
              <br />
              <Tree
                key={rootPosition.incidentSeat}
                label={buildSeatBox({
                  positionId: rootPosition.positionId,
                  seatName: rootPosition.incidentSeat,
                  members: membersForSeat,
                  isWorkteam: rootPosition.isWorkgroup,
                  setShowEditPositionModal: setShowEditPositionModal,
                })}
              >
                {buildTree(
                  rootPosition.incidentSeat,
                  incidentMembers,
                  positions,
                  showAssignedPositions,
                  setShowEditPositionModal,
                  currentSection
                )}
              </Tree>
            </>
          );
        })}
      {showEditPositionModalForPosition &&
        showEditPositionModalForPosition !== '' && (
          <EditPositionModal
            show={showEditPositionModalForPosition !== ''}
            onHide={() => setShowEditPositionModal('')}
            positionId={showEditPositionModalForPosition}
            incidentMembers={incidentMembers}
          />
        )}
    </div>
  );
};

export default IncidentRosterBlockChart;
